import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  SearchBusinessesAction,
  GetDiscoveryFiltersAction,
} from "../../store/action";

/**
 * The business search, filters and all.
 *
 * Shared by the two surfaces that offer it — the public `/discover` page and the
 * signed-in `Book` tab — because they are the same search. Splitting the
 * semantics between them is how a business that appears on one silently stops
 * appearing on the other.
 *
 * The filters live in the **URL**, not in component state alone, so a search can
 * be linked, bookmarked and reopened by the back button. That is also what makes
 * the two surfaces interchangeable: a query string means the same thing on
 * either.
 */

export const DEFAULT_PAGE_SIZE = 12;

/**
 * `sort` for "first free appointment": businesses ordered by their soonest
 * opening, those with none left out. The API's `DiscoverySearchRequest.Sort`.
 */
export const SORT_SOONEST = "soonest";

export const EMPTY_FILTERS = {
  q: "",
  city: "",
  category: "",
  date: "",
  partOfDay: "",
  sort: "",
  availableOnly: false,
  page: 1,
};

const NO_OPTIONS = { cities: [], categories: [], services: [] };

/** URL → filters. The URL is the source of truth, so this runs on every change. */
export const fromSearchParams = (params) => ({
  q: params.get("q") || "",
  city: params.get("city") || "",
  category: params.get("category") || "",
  date: params.get("date") || "",
  partOfDay: params.get("partOfDay") || "",
  sort: params.get("sort") || "",
  // Picking a day means asking who is free on it, so the toggle defaults on
  // whenever a date is present and the URL does not explicitly turn it off.
  availableOnly: params.has("availableOnly")
    ? params.get("availableOnly") === "true"
    : Boolean(params.get("date")),
  page: Number(params.get("page")) || 1,
});

/** filters → URL, dropping everything at its default so links stay readable. */
export const toSearchParams = (filters) => {
  const params = new URLSearchParams();
  ["q", "city", "category", "date", "partOfDay", "sort"].forEach((key) => {
    if (filters[key]) params.set(key, filters[key]);
  });
  if (filters.date && !filters.availableOnly) params.set("availableOnly", "false");
  if (filters.page > 1) params.set("page", String(filters.page));
  return params;
};

/**
 * @param {object}  [options]
 * @param {number}  [options.pageSize]
 * @param {string}  [options.defaultCity]
 *   A city to open on when the URL carries no search of its own — the signed-in
 *   customer's city of residence. Applied **once**, and only to an untouched
 *   URL: it is a starting point, not a filter the page keeps reasserting. Read
 *   at mount, so a caller that resolves it asynchronously should hold off
 *   mounting until it knows (otherwise the first search runs without it).
 */
export const useDiscoverySearch = ({
  pageSize = DEFAULT_PAGE_SIZE,
  defaultCity = "",
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [options, setOptions] = useState(NO_OPTIONS);
  const [results, setResults] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const urlIsUntouched = !searchParams.toString();

  // The opening city, held until it is no longer needed — which is either when
  // it has reached the URL, or when the visitor has made a filter choice of
  // their own. Both are handled below.
  //
  // State rather than a ref that an effect flips, and that distinction has
  // already caused a bug: retiring the seed the moment the mirroring effect
  // *started* left a window between then and the navigation actually landing in
  // which the city was neither in the URL nor in the seed. The search re-ran
  // with no city at all and the dropdown blinked back to "Any city". Holding it
  // in state until the URL demonstrably carries a filter closes that window,
  // and keeps the search correct even if the navigation never lands.
  const [seedCity, setSeedCity] = useState(() =>
    urlIsUntouched ? defaultCity : "",
  );

  const filters =
    seedCity && urlIsUntouched
      ? { ...fromSearchParams(searchParams), city: seedCity }
      : fromSearchParams(searchParams);

  // The canonical form of the current search, and the only thing the fetch keys
  // off. Deriving it from the resolved filters rather than from the raw URL is
  // what lets the opening city take effect on the *first* pass: mirroring it
  // into the URL a moment later produces this same string, so the search does
  // not run twice.
  const search = toSearchParams(filters).toString();

  // The search box is uncontrolled by the URL while being typed in: round-
  // tripping every keystroke through the router would push a history entry per
  // character and move the caret.
  const [queryDraft, setQueryDraft] = useState(filters.q);
  const queryDraftRef = useRef(filters.q);

  // Put the opening city in the URL, so the search it produced is linkable like
  // any other. Mount only: this is a starting point, and re-running it would
  // make the default reassert itself over the visitor's own choices.
  useEffect(() => {
    if (!seedCity || !urlIsUntouched) return;
    setSearchParams(toSearchParams({ ...EMPTY_FILTERS, city: seedCity }), {
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once the URL carries a search of its own, the seed has nothing left to do.
  // Retiring it here rather than when it was written is what makes an
  // undelivered navigation harmless: until the URL actually says "Pitesti", the
  // seed keeps saying it.
  useEffect(() => {
    if (!urlIsUntouched) setSeedCity("");
  }, [urlIsUntouched]);

  const applyFilters = useCallback(
    (next) => {
      // The visitor has chosen for themselves, so the opening city is done —
      // explicitly, because "clear filters" empties the URL and an untouched URL
      // is exactly the condition the seed applies on. Without this, clearing the
      // filters would snap the city straight back in: the page arguing with the
      // person using it.
      setSeedCity("");

      // Choosing a day is asking who is free on it, so the availability filter
      // switches itself on the moment a date appears. Turning it back off stays
      // the visitor's call, and survives because the URL then says so
      // explicitly.
      const dateJustPicked = next.date && !filters.date;
      const resolved = dateJustPicked ? { ...next, availableOnly: true } : next;

      setSearchParams(toSearchParams(resolved), { replace: true });
    },
    [setSearchParams, filters.date],
  );

  // A back/forward navigation changes the URL under us; follow it.
  useEffect(() => {
    const fromUrl = searchParams.get("q") || "";
    if (fromUrl !== queryDraftRef.current) {
      queryDraftRef.current = fromUrl;
      setQueryDraft(fromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    GetDiscoveryFiltersAction()
      .then((data) =>
        setOptions({
          cities: data?.cities || [],
          categories: data?.categories || [],
          services: data?.services || [],
        }),
      )
      // The dropdowns are a convenience; losing them should not take the page
      // with it, since the text box and the date still work.
      .catch(() => setOptions(NO_OPTIONS));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const active = fromSearchParams(new URLSearchParams(search));

    setLoading(true);
    SearchBusinessesAction({ ...active, pageSize })
      .then((data) => {
        if (cancelled) return;
        setResults({ items: data?.items || [], total: data?.total || 0 });
        setError(false);
      })
      .catch(() => {
        if (cancelled) return;
        setResults({ items: [], total: 0 });
        // A flag, not a translated string: holding the message here would make
        // this effect depend on `t` and re-run the whole search every time the
        // visitor switches language.
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [search, pageSize]);

  // Typing settles before it reaches the URL, so one word is one search rather
  // than one per letter.
  useEffect(() => {
    if (queryDraft === (searchParams.get("q") || "")) return;

    const timer = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      if (queryDraft) params.set("q", queryDraft);
      else params.delete("q");
      params.delete("page");
      setSearchParams(params, { replace: true });
    }, 350);

    return () => clearTimeout(timer);
  }, [queryDraft, searchParams, setSearchParams]);

  /** What `DiscoverFilters` reports back: a draft query is handled locally. */
  const handleFiltersChange = useCallback(
    (next) => {
      if (next.q !== queryDraft) {
        queryDraftRef.current = next.q;
        setQueryDraft(next.q);
        return;
      }
      applyFilters(next);
    },
    [applyFilters, queryDraft],
  );

  const reset = useCallback(() => {
    queryDraftRef.current = "";
    setQueryDraft("");
    applyFilters(EMPTY_FILTERS);
  }, [applyFilters]);

  const goToPage = useCallback(
    (page) => applyFilters({ ...filters, page }),
    [applyFilters, filters],
  );

  return {
    filters,
    /** The filters as the controls want them — the query still being typed. */
    values: { ...filters, q: queryDraft },
    options,
    results,
    loading,
    error,
    totalPages: Math.max(1, Math.ceil(results.total / pageSize)),
    handleFiltersChange,
    reset,
    goToPage,
  };
};
